/**
 * @module ReasoningNotStartedState
 * @purpose Body state for the reasoning panel when no run has ever been
 *   created for this case -- distinct from the empty state (a completed run
 *   that surfaced zero clusters).
 * @depends react-i18next
 * @dependedOnBy CanvasReasoningPanel
 *
 * @rules
 * - Performance: pure display -- no data fetching
 * - State: none
 * - Errors: n/a
 */

import { useTranslation } from 'react-i18next'

interface ReasoningNotStartedStateProps {
  /** Opens the launch confirmation for a first run. */
  onLaunch: () => void
}

/**
 * Renders the reasoning panel's never-reasoned state.
 *
 * @behaviour No `busy` prop: the instant a launch starts, the panel's body
 *   swaps to the running state and this component unmounts -- that swap is
 *   the busy feedback, so this component carries none of its own.
 * @errors n/a -- purely presentational
 */
export function ReasoningNotStartedState({
  onLaunch,
}: ReasoningNotStartedStateProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-8">
      <p className="text-sm" style={{ color: 'var(--text)' }}>
        {t('reasoning.notStarted.title')}
      </p>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {t('reasoning.notStarted.subtitle')}
      </p>
      <button
        type="button"
        onClick={onLaunch}
        className="mt-1 text-xs uppercase tracking-wide underline hover:no-underline"
        style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}
      >
        {t('reasoning.notStarted.cta')}
      </button>
    </div>
  )
}
